import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Calculator,
  List,
  Plus,
  X,
  User,
  Scale,
  Target,
  LogOut,
  Loader2,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import CompanyLogo from './common/CompanyLogo';
import { APP_TITLE, COMPANY_NAME } from '@/lib/brand';
import { authApi, clearAuthToken } from '@/lib/api/auth';

interface SidebarProps {
  open?: boolean;
  collapsed?: boolean;
  onClose?: () => void;
  onToggleCollapsed?: () => void;
  userName?: string;
  onOpenCalculator?: () => void;
  onOpenAmountCalculator?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  open = true,
  collapsed = false,
  onClose,
  onToggleCollapsed,
  userName: userNameProp,
  onOpenCalculator,
  onOpenAmountCalculator,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [userName, setUserName] = useState(userNameProp || '');
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    if (open && onClose && typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- فقط با تغییر مسیر روی موبایل ببند
  }, [location.pathname]);

  useEffect(() => {
    if (userNameProp) {
      setUserName(userNameProp);
      return;
    }
    let cancelled = false;
    authApi
      .fetchUser()
      .then((u) => {
        if (!cancelled) setUserName(u.name || u.username || '');
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [userNameProp]);

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await authApi.logout();
    } catch {
      clearAuthToken();
    } finally {
      navigate('/login', { replace: true });
      setLoggingOut(false);
    }
  };

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-2.5 px-2.5 py-2 mx-1.5 rounded-lg text-[13px] transition-all min-w-0 ${
      collapsed ? 'justify-center' : ''
    } ${
      isActive
        ? 'bg-white/15 ring-1 ring-white/15 font-semibold text-white'
        : 'text-gray-100 hover:bg-white/10'
    }`;

  return (
    <>
      {open && onClose && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} aria-hidden="true" />
      )}
      <aside
        className={`
          relative bg-[#082c1a] text-white flex flex-col shrink-0 overflow-hidden
          fixed lg:relative inset-y-0 right-0 z-50 lg:z-auto
          transform transition-[transform,width] duration-200 ease-out
          ${open ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}
          ${collapsed ? 'lg:w-[4.5rem] w-[13.5rem]' : 'w-[15.5rem]'}
        `}
        dir="rtl"
      >
        <div className={`relative px-3 py-4 border-b border-white/10 flex items-center ${collapsed ? 'justify-center' : 'justify-between'} gap-1.5 min-w-0`}>
          <div className={`min-w-0 ${collapsed ? 'hidden lg:hidden' : 'flex-1 overflow-hidden'}`}>
            <CompanyLogo
              className="mb-2 flex justify-start"
              imgClassName="h-8 w-auto max-w-[9rem] object-contain brightness-0 invert"
            />
            <h1 className="font-bold text-sm leading-snug truncate">{COMPANY_NAME}</h1>
            <p className="text-[11px] text-white/70 mt-0.5 truncate">{APP_TITLE}</p>
          </div>
          {collapsed && (
            <CompanyLogo className="hidden lg:flex" imgClassName="h-8 w-8 object-contain brightness-0 invert" />
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-white/10 lg:hidden shrink-0"
              aria-label="بستن منو"
            >
              <X size={18} />
            </button>
          )}
        </div>

        <div className={`relative px-3 py-3 border-b border-white/10 flex items-center gap-2 min-w-0 ${collapsed ? 'justify-center' : ''}`}>
          <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center shrink-0">
            <User size={14} />
          </div>
          {!collapsed && (
            <div className="min-w-0 overflow-hidden">
              <span className="text-[11px] text-white/60 block">کاربر</span>
              <span className="text-[13px] text-white font-bold block truncate">{userName || 'پنل آنالیز'}</span>
            </div>
          )}
        </div>

        <nav className="relative flex-1 overflow-y-auto overflow-x-hidden py-3 min-w-0">
          {!collapsed && (
            <div className="px-3 py-1 text-[10px] font-bold text-white/40 uppercase tracking-wider">اصلی</div>
          )}
          <NavLink to="/" end className={navClass} title="داشبورد">
            <LayoutDashboard size={16} className="shrink-0 opacity-80" />
            {!collapsed && <span className="truncate">داشبورد</span>}
          </NavLink>
          <NavLink to="/analyses" end className={navClass} title="لیست آنالیزها">
            <List size={16} className="shrink-0 opacity-80" />
            {!collapsed && <span className="truncate">لیست آنالیزها</span>}
          </NavLink>
          <NavLink to="/analyses/create" end className={navClass} title="آنالیز جدید">
            <Plus size={16} className="shrink-0 opacity-80" />
            {!collapsed && <span className="truncate">آنالیز جدید</span>}
          </NavLink>

          {!collapsed && (
            <div className="px-3 py-1.5 mt-3 text-[10px] font-bold text-white/40 uppercase tracking-wider">مناقصه</div>
          )}
          <NavLink to="/bid-domain" end className={navClass} title="تعیین دامنه">
            <Target size={16} className="shrink-0 opacity-80" />
            {!collapsed && <span className="truncate">تعیین دامنه</span>}
          </NavLink>

          {!collapsed && (
            <div className="px-3 py-1.5 mt-3 text-[10px] font-bold text-white/40 uppercase tracking-wider">ابزارها</div>
          )}
          <button
            type="button"
            onClick={onOpenCalculator}
            title="ماشین حساب"
            className={`flex items-center gap-2.5 px-2.5 py-2 mx-1.5 rounded-lg text-[13px] text-gray-100 hover:bg-white/10 min-w-0 ${collapsed ? 'justify-center w-[calc(100%-0.75rem)]' : 'w-[calc(100%-0.75rem)]'}`}
          >
            <Calculator size={16} className="shrink-0 opacity-80" />
            {!collapsed && <span className="truncate">ماشین حساب</span>}
          </button>
          <button
            type="button"
            onClick={onOpenAmountCalculator}
            title="مبلغ / ضریب"
            className={`flex items-center gap-2.5 px-2.5 py-2 mx-1.5 rounded-lg text-[13px] text-gray-100 hover:bg-white/10 min-w-0 ${collapsed ? 'justify-center w-[calc(100%-0.75rem)]' : 'w-[calc(100%-0.75rem)]'}`}
          >
            <Scale size={16} className="shrink-0 opacity-80" />
            {!collapsed && <span className="truncate">مبلغ / ضریب</span>}
          </button>
        </nav>

        <div className="relative p-2.5 border-t border-white/10 min-w-0 space-y-2">
          {onToggleCollapsed && (
            <button
              type="button"
              onClick={onToggleCollapsed}
              className="hidden lg:flex w-full items-center justify-center gap-2 rounded-lg bg-white/5 hover:bg-white/10 px-3 py-2 text-[12px] text-white/80"
              title={collapsed ? 'باز کردن منو' : 'جمع کردن منو'}
            >
              {collapsed ? <ChevronsLeft size={16} /> : <ChevronsRight size={16} />}
              {!collapsed && 'جمع کردن'}
            </button>
          )}
          <button
            type="button"
            onClick={handleLogout}
            disabled={loggingOut}
            className={`flex w-full items-center gap-2 rounded-lg bg-white/10 hover:bg-rose-500/25 border border-white/10 hover:border-rose-300/40 px-3 py-2 text-[13px] font-semibold text-white transition-colors disabled:opacity-60 ${collapsed ? 'justify-center' : 'justify-center'}`}
          >
            {loggingOut ? <Loader2 size={15} className="animate-spin" /> : <LogOut size={15} />}
            {!collapsed && 'خروج'}
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
